import React from 'react';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Grid,
  Paper,
  ImageList,
  ImageListItem,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getBlogPostBySlug } from './blogData';
import { SITE_URL } from '../../../utils/site';

const HeroImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '400px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '150px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
  },
  [theme.breakpoints.down('md')]: {
    height: '300px',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  '& h2': {
    fontSize: '1.75rem',
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
  },
  '& strong': {
    color: theme.palette.text.primary,
  },
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  marginBottom: theme.spacing(2),
  '& video': {
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    maxHeight: '400px',
    objectFit: 'contain',
    backgroundColor: '#000',
  },
}));

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC', // dates are stored as YYYY-MM-DD; without this they show a day early in US time zones
    });
  };

  // Inline formatting: **bold** and [link text](/path) links
  const renderInline = (text: string) =>
    text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (link) {
        const [, label, href] = link;
        return href.startsWith('/') ? (
          <Link key={i} component={RouterLink} to={href}>{label}</Link>
        ) : (
          <Link key={i} href={href} target="_blank" rel="noopener noreferrer">{label}</Link>
        );
      }
      return part;
    });

  // Simple markdown-like parser for the content
  const renderContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;

    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListTag = listType === 'ul' ? 'ul' : 'ol';
        elements.push(
          <ListTag key={elements.length}>
            {currentList.map((item, i) => (
              <li key={i}>{renderInline(item)}</li>
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <Typography variant="h2" component="h2" key={index}>
            {trimmed.replace('## ', '')}
          </Typography>
        );
      } else if (trimmed.startsWith('- **') || trimmed.startsWith('- ')) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        // Handle bold text in list items
        let itemText = trimmed.replace(/^- /, '');
        currentList.push(itemText);
      } else if (/^\d+\. /.test(trimmed)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(trimmed.replace(/^\d+\. /, ''));
      } else if (trimmed === '') {
        flushList();
      } else {
        flushList();
        elements.push(
          <Typography variant="body1" component="p" key={index}>
            {renderInline(trimmed)}
          </Typography>
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <>
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={`${SITE_URL}${post.featuredImage}`} />
        <meta property="og:type" content="article" />
      </Helmet>

      <HeroImage sx={{ backgroundImage: `url(${post.featuredImage})` }} />

      <Container sx={{ mt: -8, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to Blog
          </Button>

          <Box sx={{ mb: 3 }}>
            <Chip label={post.category} color="primary" sx={{ mr: 1 }} />
            {post.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                variant="outlined"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>

          <Typography
            variant="h3"
            component="h1"
            fontWeight={700}
            sx={{ mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {post.title}
          </Typography>

          <Box display="flex" alignItems="center" sx={{ mb: 4, color: 'text.secondary' }}>
            <CalendarTodayIcon sx={{ fontSize: 18, mr: 1 }} />
            <Typography variant="body2">{formatDate(post.date)}</Typography>
          </Box>

          <ContentSection>{renderContent(post.content)}</ContentSection>

          {/* Image Gallery */}
          {post.images && post.images.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h5" component="h3" fontWeight={600} gutterBottom>
                Project Photos
              </Typography>
              <ImageList
                sx={{ width: '100%', mt: 2 }}
                cols={3}
                gap={16}
                variant="quilted"
              >
                {post.images.map((image, index) => (
                  <ImageListItem
                    key={index}
                    cols={index === 0 ? 2 : 1}
                    rows={index === 0 ? 2 : 1}
                  >
                    <img
                      src={image}
                      alt={`Project photo ${index + 1}`}
                      loading="lazy"
                      style={{
                        borderRadius: 8,
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}

          {/* Videos */}
          {post.videos && post.videos.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h5" component="h3" fontWeight={600} gutterBottom>
                Project Videos
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {post.videos.map((video, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <VideoWrapper>
                      <video controls preload="metadata">
                        <source src={video} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </VideoWrapper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* CTA Section */}
          <Paper
            sx={{
              mt: 6,
              p: 4,
              textAlign: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Need Help With a Similar Project?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Contact JBR Delivery for professional furniture delivery and installation services.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{ fontWeight: 600 }}
            >
              Get a Free Quote
            </Button>
          </Paper>
        </Paper>
      </Container>

      <Box sx={{ py: 4 }} />
    </>
  );
};

export default BlogPostPage;
